import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
const MARKUP_OPTIONS = [0, 5, 10, 12.5, 15, 17.5, 20, 22.5, 25, 27.5, 30, 32.5, 35, 37.5, 40, 45, 50];

export default function MarkupFields({ form, setForm, templates }) {
  const cost = parseFloat(form.amount) || 0;
  const markupAPct = parseFloat(form.markup_a_pct) || 0;
  const markupBPct = parseFloat(form.markup_b_pct) || 0;
  const markupAAmount = cost * (markupAPct / 100);
  const subtotal = cost + markupAAmount;
  const markupBAmount = subtotal * (markupBPct / 100);
  const total = subtotal + markupBAmount;

  const applyTemplate = (t) => {
    setForm(p => ({
      ...p,
      markup_a_pct: t.markup_a_pct,
      markup_b_pct: t.markup_b_pct,
      markup_a_label: t.markup_a_label,
      markup_b_label: t.markup_b_label,
    }));
  };

  return (
    <div className="border border-border rounded-md p-3 bg-muted/10 space-y-3" data-testid="markup-fields">
      <div className="flex items-center justify-between">
        <Label className="text-xs font-semibold">Quote Markup</Label>
        {templates.length > 0 && (
          <Select onValueChange={(v) => { const t = templates.find(t => t.id === v); if (t) applyTemplate(t); }}>
            <SelectTrigger className="w-[170px] h-7 text-[10px]" data-testid="markup-template-select"><SelectValue placeholder="Apply Template..." /></SelectTrigger>
            <SelectContent>
              {templates.map(t => (
                <SelectItem key={t.id} value={t.id} className="text-xs">{t.name} ({t.markup_a_pct}% / {t.markup_b_pct}%)</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] text-muted-foreground">Markup A Label</Label>
          <Input
            value={form.markup_a_label || "Our Mark Up"}
            onChange={e => setForm(p => ({ ...p, markup_a_label: e.target.value }))}
            className="h-7 text-xs mt-0.5"
            data-testid="markup-a-label-input"
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Markup A (%)</Label>
          <Select value={String(form.markup_a_pct || 0)} onValueChange={v => setForm(p => ({ ...p, markup_a_pct: parseFloat(v) }))}>
            <SelectTrigger className="h-7 text-xs mt-0.5" data-testid="markup-a-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MARKUP_OPTIONS.map(o => <SelectItem key={o} value={String(o)} className="text-xs">{o}%</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Markup B Label</Label>
          <Input
            value={form.markup_b_label || "Client Mark Up"}
            onChange={e => setForm(p => ({ ...p, markup_b_label: e.target.value }))}
            className="h-7 text-xs mt-0.5"
            data-testid="markup-b-label-input"
          />
        </div>
        <div>
          <Label className="text-[10px] text-muted-foreground">Markup B (%)</Label>
          <Select value={String(form.markup_b_pct || 0)} onValueChange={v => setForm(p => ({ ...p, markup_b_pct: parseFloat(v) }))}>
            <SelectTrigger className="h-7 text-xs mt-0.5" data-testid="markup-b-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {MARKUP_OPTIONS.map(o => <SelectItem key={o} value={String(o)} className="text-xs">{o}%</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Live Calculation Preview */}
      {cost > 0 && (markupAPct > 0 || markupBPct > 0) && (
        <div className="border border-border rounded-md overflow-hidden text-xs" data-testid="markup-preview">
          <div className="px-3 py-1.5 bg-muted/40 border-b border-border">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Live Preview</p>
          </div>
          <div className="px-3 py-2 space-y-1">
            <div className="flex justify-between">
              <span>Cost</span>
              <span className="font-mono font-medium">£{cost.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
            </div>
            {markupAPct > 0 && (
              <>
                <div className="flex justify-between text-primary">
                  <span>+ {form.markup_a_label || "Markup A"} ({markupAPct}%)</span>
                  <span className="font-mono">£{markupAAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                </div>
                <div className="flex justify-between border-t border-border/50 pt-1 font-semibold">
                  <span>Subtotal</span>
                  <span className="font-mono">£{subtotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
                </div>
              </>
            )}
            {markupBPct > 0 && (
              <div className="flex justify-between text-violet-600 dark:text-violet-400">
                <span>+ {form.markup_b_label || "Markup B"} ({markupBPct}%)</span>
                <span className="font-mono">£{markupBAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
              </div>
            )}
            <div className="flex justify-between border-t border-border pt-1 font-bold text-sm">
              <span>Total (to Client)</span>
              <span className="font-mono">£{total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
            </div>
          </div>
          {markupAPct > 0 && (
            <div className="px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 border-t border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">Our Profit</span>
                <span className="font-bold font-mono text-emerald-700 dark:text-emerald-400">£{markupAAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
